import * as React from "react"
import { Link as RouterLink, useSearchParams } from "react-router-dom"
import { Plus, Copy, Filter, Upload } from "lucide-react"
import { TransactionList } from "@/components/expenses/transaction-list"
import { ExpenseForm } from "@/components/expenses/expense-form"
import { SettlementForm } from "@/components/settlements/settlement-form"
import { Button } from "@/components/ui/button"
import { ResponsiveContainer } from "@/components/layout/responsive-grid"
import { LoadingState } from "@/components/ui/loading-state"
import { SyncStatusIndicator } from "@/components/sync-status-indicator"
import { randomUUID } from "@/lib/uuid"
import { useAuthStore } from "@/stores/auth.store"
import {
  useGroupTransactions,
  useCreateTransaction,
  useUpdateTransaction,
  useDeleteTransaction,
  useSyncGroup,
  usePushLocalChanges,
  useRepositoryLogger,
  useSyncStatus
} from "@/hooks/repository"
import { useGroupCategories } from "@/hooks/queries/useCategories"
import { useGroupUsers } from "@/hooks/queries/useGroups"
import { useActiveGroup } from "@/stores/group.store"
import { useCategoryBalances } from "@/hooks/queries/useCategoryBalances"
import type { Expense } from "@/types/domain.types"
import { useExpenseForm } from "@/contexts/expense-form.context"
import { toast } from "sonner"
import type { TransactionRecord } from "@/services/storage/storage.service"
import type { Transaction } from "@/api/schemas/transaction.schema"
import { useSetSyncBackoff } from "@/contexts/sync-backoff.context"

// Helper to transform repository TransactionRecords to UI Transaction type
// Prefers enriched data from the record, falls back to lookups only when needed
function transformToUITransaction(
  record: TransactionRecord,
  categories: any[],
  members: any[]
): Transaction {
  // Only build lookup maps if we need them (for local-only transactions without enriched data)
  const categoryMap = new Map(categories.map(c => [c.id, c]))
  const memberMap = new Map(members.map(m => [m.id, m]))

  const base = {
    id: record.id,
    type: record.type,
    amount: record.amount || 0,
    currency: record.currency_code || 'EUR',
    date: record.date || record.created_at || new Date().toISOString(),
    description: record.description || null,
  }

  if (record.type === 'expense') {
    return {
      ...base,
      paid_by: record.paid_by,
      created_by: record.created_by,
      // Prefer enriched user data from sync, fall back to lookup
      user: record.user || memberMap.get(record.paid_by || '') || {
        id: record.paid_by || '',
        name: 'Unknown'
      },
      // Prefer enriched category data from sync, fall back to lookup
      category: record.category || categoryMap.get(record.category_id || '') || {
        id: record.category_id || '',
        name: 'Uncategorized',
        emoji: '📝'
      },
    }
  }

  if (record.type === 'pool_event') {
    return {
      ...base,
      type: 'pool_event',
      event_type: record.event_type,
      pool_id: record.pool_id,
      pool_name: record.pool_name,
      expense_id: record.expense_id ?? null,
      user: record.user || {
        id: '',
        name: 'Unknown'
      },
    }
  }

  // settlement - prefer enriched user data
  return {
    ...base,
    kind: record.kind,
    origin_event_id: record.origin_event_id ?? null,
    from_user: record.from_user || memberMap.get(record.from_user_id || '') || {
      id: record.from_user_id || '',
      name: 'Unknown'
    },
    to_user: record.to_user || memberMap.get(record.to_user_id || '') || {
      id: record.to_user_id || '',
      name: 'Unknown'
    },
  }
}

export const ExpensesPageOffline: React.FC = () => {
  const { user } = useAuthStore()
  const { activeGroupId } = useActiveGroup()
  const { isOpen: showExpenseForm, openAddExpenseForm, closeExpenseForm } = useExpenseForm()
  const [editingExpense, setEditingExpense] = React.useState<Expense | undefined>()
  const [viewMode, setViewMode] = React.useState<'transactions' | 'expenses'>('transactions')
  const [searchParams, setSearchParams] = useSearchParams()
  // Transaction to scroll to + briefly highlight, e.g. ?highlight=<id> from
  // global search or a deep link from the recurring page.
  const [highlightId, setHighlightId] = React.useState<string | null>(null)
  const [showSettlementForm, setShowSettlementForm] = React.useState(false)
  const [showFilters, setShowFilters] = React.useState(false)

  // Use new repository hooks
  const logger = useRepositoryLogger()
  const setSyncBackoff = useSetSyncBackoff()
  const { data: allTransactions = [], isLoading: isLoadingExpenses, error: expensesError } = useGroupTransactions(activeGroupId || "", {
    enabled: !!activeGroupId
  })

  const { mutateAsync: createTransactionAsync, isLoading: isCreating } = useCreateTransaction()
  const { mutateAsync: updateTransactionAsync } = useUpdateTransaction()
  const { mutate: deleteTransaction } = useDeleteTransaction()
  const { mutateAsync: syncGroupAsync, isLoading: isSyncing } = useSyncGroup()
  const { mutateAsync: pushLocalChangesAsync } = usePushLocalChanges()

  // Ensure allTransactions is always an array
  const transactionRecords = allTransactions || []

  // Get sync status from repository
  const syncStatus = useSyncStatus(activeGroupId || '')
  const isOnline = navigator.onLine

  const syncNow = React.useCallback(async () => {
    if (activeGroupId) {
      logger.info('expenses-offline', 'Manual sync triggered', { groupId: activeGroupId })
      try {
        // First, push any local changes to the server
        logger.info('expenses-offline', 'Pushing local changes first', { groupId: activeGroupId })
        const pushResult = await pushLocalChangesAsync(activeGroupId)
        logger.info('expenses-offline', 'Push complete', {
          pushed: pushResult.pushed,
          failed: pushResult.failed
        })

        // Then, sync from server to get latest data
        const result = await syncGroupAsync({ groupId: activeGroupId, mode: 'full' })
        logger.info('expenses-offline', 'Sync successful', {
          recordsSynced: result.recordsSynced,
          duration: result.duration
        })

        const totalSynced = pushResult.pushed + result.recordsSynced
        toast.success(`Synced ${totalSynced} transactions (${pushResult.pushed} pushed, ${result.recordsSynced} fetched)`)
      } catch (error) {
        logger.error('expenses-offline', 'Sync failed', {}, error as Error)
        toast.error('Sync failed: ' + (error as Error).message)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, syncGroupAsync, pushLocalChangesAsync])

  // Periodic sync with exponential backoff - use refs to avoid re-render loops
  const lastSyncAttemptRef = React.useRef<number>(0)
  const syncFailureCountRef = React.useRef<number>(0)
  const nextSyncTimeRef = React.useRef<number>(0)

  // Track backoff state separately for UI updates only
  const [backoffState, setBackoffState] = React.useState({ failureCount: 0, nextSyncTime: 0 })

  // Update global backoff state for dev inspector
  React.useEffect(() => {
    setSyncBackoff(backoffState)
  }, [backoffState, setSyncBackoff])

  // Calculate backoff delay based on failure count
  const getBackoffDelay = (failureCount: number): number => {
    if (failureCount === 0) return 0
    // Exponential backoff: 5s, 10s, 20s, 40s, 60s (max)
    const baseDelay = 5000 // 5 seconds
    const maxDelay = 60000 // 60 seconds
    const delay = Math.min(baseDelay * Math.pow(2, failureCount - 1), maxDelay)
    return delay
  }

  // Initial sync on group change
  React.useEffect(() => {
    if (!activeGroupId || isLoadingExpenses || isSyncing) return

    const now = Date.now()

    // Skip if we're in backoff period
    if (now < nextSyncTimeRef.current) {
      const waitTime = Math.ceil((nextSyncTimeRef.current - now) / 1000)
      logger.info('expenses-offline', 'Skipping sync due to backoff', {
        groupId: activeGroupId,
        waitSeconds: waitTime
      })
      return
    }

    // Only sync if this is a new group or enough time has passed
    const timeSinceLastSync = now - lastSyncAttemptRef.current
    const shouldSync = timeSinceLastSync > 30000 // At least 30 seconds between syncs

    if (shouldSync) {
      logger.info('expenses-offline', 'Triggering sync', { groupId: activeGroupId })
      lastSyncAttemptRef.current = now

      syncGroupAsync({ groupId: activeGroupId, mode: 'full' })
        .then(result => {
          logger.info('expenses-offline', 'Sync successful', {
            recordsSynced: result.recordsSynced,
            duration: result.duration
          })
          // Reset failure count on success
          syncFailureCountRef.current = 0
          nextSyncTimeRef.current = 0
          setBackoffState({ failureCount: 0, nextSyncTime: 0 })
        })
        .catch(error => {
          const newFailureCount = syncFailureCountRef.current + 1
          const backoffDelay = getBackoffDelay(newFailureCount)
          const nextSync = now + backoffDelay

          logger.error('expenses-offline', 'Sync failed', {
            failureCount: newFailureCount,
            backoffSeconds: Math.ceil(backoffDelay / 1000),
            nextSyncAt: new Date(nextSync).toISOString()
          }, error as Error)

          syncFailureCountRef.current = newFailureCount
          nextSyncTimeRef.current = nextSync
          setBackoffState({ failureCount: newFailureCount, nextSyncTime: nextSync })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, isLoadingExpenses, isSyncing, syncGroupAsync])

  // Periodic background sync every 5 minutes (if not in backoff)
  // Also handles retry after backoff period expires
  React.useEffect(() => {
    if (!activeGroupId) return

    // Check every 5 seconds if we should sync
    const checkInterval = setInterval(() => {
      const now = Date.now()

      // Skip if already syncing
      if (isSyncing) return

      const timeSinceLastSync = now - lastSyncAttemptRef.current
      const isBackoffExpired = now >= nextSyncTimeRef.current

      // Retry after backoff expires (if we had failures)
      if (syncFailureCountRef.current > 0 && isBackoffExpired) {
        logger.info('expenses-offline', 'Backoff expired, retrying sync', {
          groupId: activeGroupId,
          previousFailures: syncFailureCountRef.current
        })
        lastSyncAttemptRef.current = now

        syncGroupAsync({ groupId: activeGroupId, mode: 'incremental' })
          .then(result => {
            logger.info('expenses-offline', 'Retry sync successful', {
              recordsSynced: result.recordsSynced,
              duration: result.duration
            })
            syncFailureCountRef.current = 0
            nextSyncTimeRef.current = 0
            setBackoffState({ failureCount: 0, nextSyncTime: 0 })
          })
          .catch(error => {
            const newFailureCount = syncFailureCountRef.current + 1
            const backoffDelay = getBackoffDelay(newFailureCount)
            const nextSync = now + backoffDelay

            logger.warn('expenses-offline', 'Retry sync failed', {
              failureCount: newFailureCount,
              backoffSeconds: Math.ceil(backoffDelay / 1000)
            }, error as Error)

            syncFailureCountRef.current = newFailureCount
            nextSyncTimeRef.current = nextSync
            setBackoffState({ failureCount: newFailureCount, nextSyncTime: nextSync })
          })
        return
      }

      // Regular periodic sync every 5 minutes (if no failures and not in backoff)
      if (syncFailureCountRef.current === 0 && timeSinceLastSync >= 5 * 60 * 1000) {
        logger.info('expenses-offline', 'Periodic sync triggered', { groupId: activeGroupId })
        lastSyncAttemptRef.current = now

        syncGroupAsync({ groupId: activeGroupId, mode: 'incremental' })
          .then(result => {
            logger.info('expenses-offline', 'Periodic sync successful', {
              recordsSynced: result.recordsSynced,
              duration: result.duration
            })
            syncFailureCountRef.current = 0
            nextSyncTimeRef.current = 0
            setBackoffState({ failureCount: 0, nextSyncTime: 0 })
          })
          .catch(error => {
            const newFailureCount = 1
            const backoffDelay = getBackoffDelay(newFailureCount)
            const nextSync = now + backoffDelay

            logger.warn('expenses-offline', 'Periodic sync failed', {
              failureCount: newFailureCount,
              backoffSeconds: Math.ceil(backoffDelay / 1000)
            }, error as Error)

            syncFailureCountRef.current = newFailureCount
            nextSyncTimeRef.current = nextSync
            setBackoffState({ failureCount: newFailureCount, nextSyncTime: nextSync })
          })
      }
    }, 5000) // Check every 5 seconds

    return () => clearInterval(checkInterval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupId, isSyncing, syncGroupAsync])

  // Sync errors are already logged in the catch handlers above with proper context
  // (failure count, backoff time, etc.), so we don't need a separate error logger here

  // Still use React Query for categories and members (read-only data)
  const { data: categories = [], isLoading: isLoadingCategories } = useGroupCategories(
    activeGroupId || ""
  )
  const { data: groupMembers = [], isLoading: isLoadingMembers } = useGroupUsers(
    activeGroupId || ""
  )

  // Fetch category balances for pinned categories
  const {
    data: categoryBalancesData,
    isLoading: isLoadingCategoryBalances,
    error: categoryBalancesError,
    refetch: refetchCategoryBalances,
  } = useCategoryBalances(activeGroupId || "")

  // Debug logging with new logger
  React.useEffect(() => {
    logger.info('expenses-offline', 'Categories loaded', { count: categories.length });
    logger.info('expenses-offline', 'Group members loaded', { count: groupMembers.length });
    logger.info('expenses-offline', 'Category balances', {
      hasData: !!categoryBalancesData,
      loading: isLoadingCategoryBalances,
      hasError: !!categoryBalancesError,
      activeGroupId
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, groupMembers, categoryBalancesData, isLoadingCategoryBalances, categoryBalancesError, activeGroupId]);

  // Transform repository transactions to UI format
  const transactions: Transaction[] = React.useMemo(() =>
    transactionRecords.map(record => transformToUITransaction(record, categories, groupMembers)),
    [transactionRecords, categories, groupMembers]
  )

  // Deep-link / search highlight: pick up ?highlight=<id>, switch to the full
  // transactions view so any type is visible, then strip the param so a
  // refresh doesn't re-trigger it.
  React.useEffect(() => {
    const requested = searchParams.get('highlight')
    if (!requested) return
    setHighlightId(requested)
    setViewMode('transactions')
    const next = new URLSearchParams(searchParams)
    next.delete('highlight')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // Scroll to the highlighted row once it is in the list (guarded so it only
  // scrolls once per highlight, not on every sync-driven data refresh).
  const scrolledForRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!highlightId) {
      scrolledForRef.current = null
      return
    }
    if (scrolledForRef.current === highlightId) return
    const el = document.getElementById(`txn-${highlightId}`)
    if (el) {
      scrolledForRef.current = highlightId
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }))
    }
  }, [highlightId, transactions])

  // Clear the highlight after a moment so the ring fades and doesn't linger.
  React.useEffect(() => {
    if (!highlightId) return
    const timer = setTimeout(() => setHighlightId(null), 2600)
    return () => clearTimeout(timer)
  }, [highlightId])

  // Refetch category balances when transactions change
  const prevTransactionCountRef = React.useRef(transactionRecords.length);
  React.useEffect(() => {
    // Only refetch if the count actually changed (not on initial load)
    if (prevTransactionCountRef.current !== 0 &&
        prevTransactionCountRef.current !== transactionRecords.length &&
        activeGroupId) {
      logger.info('expenses-offline', 'Refetching category balances due to transaction change', {
        prevCount: prevTransactionCountRef.current,
        newCount: transactionRecords.length
      });
      refetchCategoryBalances();
    }
    prevTransactionCountRef.current = transactionRecords.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRecords.length, activeGroupId])
  const isLoadingTransactions = isLoadingExpenses
  const transactionsError = expensesError

  // Debug: Log transactions
  React.useEffect(() => {
    if (transactions.length > 0) {
      logger.info('expenses-offline', 'Transactions loaded', {
        count: transactions.length,
        sample: transactions.slice(0, 2).map(t => ({ id: t.id, type: t.type, amount: t.amount }))
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactions]);

  const isLoading = viewMode === 'transactions'
    ? isLoadingTransactions || isLoadingCategories || isLoadingMembers
    : isLoadingExpenses || isLoadingCategories || isLoadingMembers

  // Show prompt if no group is selected
  if (!activeGroupId) {
    return (
      <ResponsiveContainer size="xl">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="mb-4">
            <svg
              className="mx-auto h-12 w-12 text-muted-foreground"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold mb-2">No Group Selected</h3>
          <p className="text-muted-foreground mb-4">
            Please select a group from the dropdown above to view and manage expenses.
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  const handleAddExpense = () => {
    setEditingExpense(undefined)
    openAddExpenseForm()
  }

  const handleAddSettlement = () => {
    setShowSettlementForm(true)
  }

  const handleEditExpense = (expense: Expense) => {
    setEditingExpense(expense)
    openAddExpenseForm()
  }

  const handleDeleteExpense = async (expenseId: string) => {
    if (confirm("Are you sure you want to delete this expense?")) {
      if (!activeGroupId) {
        toast.error("No active group")
        return
      }
      deleteTransaction({ id: expenseId, groupId: activeGroupId })
      toast.success("Expense deleted")
    }
  }

  const handleSubmitExpense = async (data: {
    description: string;
    amount: number;
    currency: string;
    date: string;
    category_id?: string;
    paid_by_user_id: string;
    splits: Array<{
      user_id: string;
      amount: number;
      weight?: number;
    }>;
    // Present only when the user explicitly chose a custom split in the form;
    // pushed to the server and used verbatim instead of derived splits.
    custom_splits?: Array<{ user_id: string; weight: number }>;
  }) => {
    if (!activeGroupId) {
      toast.error("Please select a group first")
      return
    }

    try {
      const submittedDate = data.date ? new Date(data.date).toISOString() : undefined

      // Enriched weights for offline rendering of a custom split (names come
      // from the member list; the server echoes the same shape after sync).
      const customSplitFields = data.custom_splits?.length
        ? {
            splits: data.custom_splits,
            weights: data.custom_splits.map((split) => ({
              user_id: split.user_id,
              name: groupMembers.find((member) => member.id === split.user_id)?.name || "",
              weight: split.weight,
            })),
          }
        : {}

      if (editingExpense) {
        const effectiveDate = submittedDate || editingExpense.created_at || new Date().toISOString()
        // Update existing expense
        logger.info('expenses-offline', 'Updating expense', { id: editingExpense.id })
        await updateTransactionAsync({
          id: editingExpense.id,
          // Preserve the create-pending flag: editing an expense that hasn't
          // synced yet must still be pushed as an (idempotent) create, not as a
          // PUT to a server row that doesn't exist.
          pending_create: (editingExpense as any).pending_create,
          type: 'expense',
          group_id: activeGroupId,
          description: data.description,
          amount: data.amount,
          currency_code: data.currency,
          category_id: data.category_id || "",
          paid_by: data.paid_by_user_id || user?.id || "",
          created_by: editingExpense.created_by || user?.id,
          date: effectiveDate,
          created_at: effectiveDate,
          is_synced: false,
          local_changes: true,
          ...customSplitFields,
        })
        toast.success("Expense updated")
      } else {
        // Create new expense through repository with enriched data
        logger.info('expenses-offline', 'Creating expense', { amount: data.amount, description: data.description })

        // Find the category and user to embed in the transaction
        const category = categories.find(c => c.id === data.category_id)
        const paidByUser = groupMembers.find(m => m.id === (data.paid_by_user_id || user?.id))
        const effectiveDate = submittedDate || new Date().toISOString()

        await createTransactionAsync({
          // Stable client-generated id: the server create is idempotent on it,
          // so a retried push can never duplicate the expense, and there is no
          // local->server id remap after sync.
          id: randomUUID(),
          pending_create: true,
          type: 'expense',
          group_id: activeGroupId,
          description: data.description,
          amount: data.amount,
          currency_code: data.currency,
          category_id: data.category_id || "",
          paid_by: data.paid_by_user_id || user?.id || "",
          created_by: user?.id,
          date: effectiveDate,
          created_at: effectiveDate,
          is_synced: false,
          local_changes: true,

          // Embed enriched data for offline rendering
          user: paidByUser ? {
            id: paidByUser.id,
            name: paidByUser.name,
            email: paidByUser.email,
          } : undefined,
          category: category ? {
            id: category.id,
            name: category.name,
            emoji: category.emoji,
            pinned: category.pinned,
          } : undefined,
          ...customSplitFields,
        })
        toast.success("Expense created")
      }

      // Close the form after successful submission
      logger.info('expenses-offline', 'Closing form')
      closeExpenseForm()
      setEditingExpense(undefined)
    } catch (error) {
      logger.error('expenses-offline', 'Error submitting expense', {}, error as Error)
      toast.error("Failed to save expense")
    }
  }

  const handleSubmitSettlement = async (data: {
    from_user_id: string
    to_user_id: string
    amount: number
    currency_code: string
    description?: string
  }) => {
    if (!activeGroupId) {
      toast.error("Please select a group first")
      return
    }

    try {
      logger.info('expenses-offline', 'Creating settlement', { amount: data.amount })

      // Find the users to embed in the settlement
      const fromUser = groupMembers.find(m => m.id === data.from_user_id)
      const toUser = groupMembers.find(m => m.id === data.to_user_id)

      await createTransactionAsync({
        id: `local-settlement-${Date.now()}`,
        type: 'settlement',
        group_id: activeGroupId,
        amount: data.amount,
        currency_code: data.currency_code,
        from_user_id: data.from_user_id,
        to_user_id: data.to_user_id,
        description: data.description || '',
        created_at: new Date().toISOString(),
        is_synced: false,
        local_changes: true,

        // Embed enriched user data for offline rendering
        from_user: fromUser ? {
          id: fromUser.id,
          name: fromUser.name,
          email: fromUser.email,
        } : undefined,
        to_user: toUser ? {
          id: toUser.id,
          name: toUser.name,
          email: toUser.email,
        } : undefined,
      })
      setShowSettlementForm(false)
      toast.success("Settlement recorded")
    } catch (error) {
      logger.error('expenses-offline', 'Error creating settlement', {}, error as Error)
      toast.error("Failed to record settlement")
    }
  }

  if (isLoading) {
    return <LoadingState message="Loading expenses..." />
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-4 sm:space-y-6">
        {/* Header with sync status */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold">Moitie</h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              Track and manage your shared expenses
            </p>
          </div>

          {/* Sync status indicator */}
          <SyncStatusIndicator
            isOnline={isOnline}
            pending={syncStatus.pending}
            failed={syncStatus.failed}
            synced={syncStatus.synced}
            onSync={syncNow}
            className="hidden sm:flex"
          />

          {/* Action buttons - visible on desktop */}
          <div className="hidden sm:flex gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <RouterLink to="/expenses/duplicates">
                <Copy className="mr-2 h-4 w-4" />
                Duplicates
              </RouterLink>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
            >
              <RouterLink to="/expenses/import">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </RouterLink>
            </Button>
            <Button
              onClick={handleAddSettlement}
              variant="outline"
              size="sm"
            >
              Record Settlement
            </Button>
            <Button
              onClick={handleAddExpense}
              size="sm"
              disabled={isCreating}
            >
              Add Expense
            </Button>
          </div>

          {/* Mobile filter toggle */}
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant="outline"
            size="sm"
            className="sm:hidden"
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile sync status */}
        <div className="sm:hidden">
          <SyncStatusIndicator
            isOnline={isOnline}
            pending={syncStatus.pending}
            failed={syncStatus.failed}
            synced={syncStatus.synced}
            onSync={syncNow}
          />
        </div>

        {/* Collapsible filters section for mobile */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row gap-2 py-3 px-3 -mx-3 bg-gray-50 rounded-lg sm:hidden">
            <Button
              variant={viewMode === 'transactions' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('transactions')}
              className="w-full"
            >
              All Transactions
            </Button>
            <Button
              variant={viewMode === 'expenses' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('expenses')}
              className="w-full"
            >
              Transactions Only
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RouterLink to="/expenses/duplicates">
                <Copy className="mr-2 h-4 w-4" />
                Duplicates
              </RouterLink>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="w-full"
            >
              <RouterLink to="/expenses/import">
                <Upload className="mr-2 h-4 w-4" />
                Import CSV
              </RouterLink>
            </Button>
            <Button
              onClick={handleAddSettlement}
              variant="outline"
              size="sm"
              className="w-full"
            >
              Record Settlement
            </Button>
          </div>
        )}

        {/* Desktop filters */}
        <div className="hidden sm:flex gap-2">
          <Button
            variant={viewMode === 'transactions' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('transactions')}
          >
            All Transactions
          </Button>
          <Button
            variant={viewMode === 'expenses' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('expenses')}
          >
            Expenses Only
          </Button>
        </div>

        {/* Main content */}
        {viewMode === 'transactions' ? (
          <TransactionList
            transactions={transactions}
            categoryBalances={categoryBalancesData?.categories || []}
            isLoading={isLoadingTransactions || isLoadingCategoryBalances}
            isError={!!transactionsError}
            onAddExpense={handleAddExpense}
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            groupBy="date"
            highlightId={highlightId ?? undefined}
          />
        ) : (
          <TransactionList
            transactions={transactions.filter(t => t.type === 'expense')}
            categoryBalances={categoryBalancesData?.categories || []}
            isLoading={isLoadingExpenses || isLoadingCategoryBalances}
            isError={!!expensesError}
            onAddExpense={handleAddExpense}
            onEditExpense={handleEditExpense}
            onDeleteExpense={handleDeleteExpense}
            groupBy="date"
            highlightId={highlightId ?? undefined}
          />
        )}

        {/* Forms */}
        <ExpenseForm
          open={showExpenseForm}
          onOpenChange={(open) => {
            if (!open) {
              closeExpenseForm()
              setEditingExpense(undefined)
            } else {
              openAddExpenseForm()
            }
          }}
          expense={editingExpense}
          categories={categories}
          groupMembers={groupMembers}
          currentUserId={user?.id || ""}
          onSubmit={handleSubmitExpense}
          isSubmitting={isCreating}
        />

        {showSettlementForm && (
          <SettlementForm
            open={showSettlementForm}
            onOpenChange={setShowSettlementForm}
            groupMembers={groupMembers}
            groupId={activeGroupId || ""}
            currentUserId={user?.id || ""}
            onSubmit={handleSubmitSettlement}
          />
        )}

        {/* Mobile Floating Action Button */}
        <div className="fixed bottom-20 right-4 z-50 sm:hidden">
          <Button
            size="lg"
            onClick={handleAddExpense}
            disabled={isCreating}
            className="h-14 w-14 rounded-full shadow-lg flex items-center justify-center p-0"
          >
            <Plus className="h-6 w-6 text-white" />
            <span className="sr-only">Add Expense</span>
          </Button>
        </div>
      </div>
    </ResponsiveContainer>
  )
}
